import { useEffect, useState } from 'react';
import { useAuthorization } from './auth/auth';
import { useDispatch, useSelector } from 'react-redux';
import { addUserDetails } from './slices/userSlice';
import { AppDispatch, RootState } from './store';
import { fetchModelConfigs } from './slices/modelSlice';
import { addChatDepartments, setChatDepartment } from '@/modules/chat/slices/chatDeptSlice';
import { addWorkflowDepartments, setWorkflowDepartment } from '@/modules/workflows/slices/workflowDeptSlice';
import { fetchTranslationLanguage } from './slices/translationSlice';
import { LazyLoadSpinner } from '@/modules/workflows/atoms/loadingSpinner';

export const Initializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user.userDetails);
  const [initialized, setInitialized] = useState(false);

  useAuthorization();

  useEffect(() => {
    if (
      import.meta.env.MODE === "development" ||
      import.meta.env.MODE === "localDev" ||
      import.meta.env.MODE === "playwright"
    ) {
      const userDetails = JSON.parse(import.meta.env.VITE_USER_DETAILS);
      if (userDetails) {
        dispatch(addUserDetails(userDetails));
      }
      return;
    }
  }, [dispatch]);

  useEffect(() => {
    initialSetup();
  }, [user])

  const initialSetup = async () => {
    if (user === null) {
      return;
    }

    try {
      await dispatch(fetchModelConfigs()).unwrap();
      const chatDepartments =
        user?.departments
          ?.filter(
            (item) =>
              Array.isArray(item.grp_access) && item.grp_access.includes("chat")
          )
          ?.map((item) => item.dpt) || [];
      dispatch(addChatDepartments(chatDepartments));
      dispatch(setChatDepartment(chatDepartments[0] || ""));

      if (user?.workflows_enabled) {
        const workflowDepartments =
          user?.departments
            ?.filter(
              (item) =>
                Array.isArray(item.grp_access) &&
                item.grp_access.includes("workflows")
            )
            ?.map((item) => item.dpt) || [];
        dispatch(addWorkflowDepartments(workflowDepartments));
        dispatch(setWorkflowDepartment(workflowDepartments[0] || ""));
      }

      if (user.translation_enabled) {
        dispatch(fetchTranslationLanguage());
      }
      setInitialized(true);
    } catch (error: any) {
      console.log(`Error occurred during start up: ${error}`)
    }
  }

  if (!initialized) {
    return <LazyLoadSpinner />;
  }

  return <>{children}</>;
}